import { Link } from 'react-router-dom';
import { ArrowRight, GitMerge, BookOpen, Compass, Check, Layers } from 'lucide-react';
import { BrandMark } from './BrandMark';
import { SiteFooter } from './SiteFooter';

export function Landing(): JSX.Element {
  return (
    <>
      <main className="marketing-page">
        <header className="marketing-nav">
          <Link to="/" className="brand">
            <BrandMark size={28} />
            ItsAllPrep
          </Link>
          <nav aria-label="Explore ItsAllPrep">
            <a href="#how-it-works">How it works</a>
            <Link to="/login">Sign in</Link>
          </nav>
        </header>
        <section className="marketing-hero">
          <div className="hero-copy">
            <p className="studio-eyebrow">YOUR OPENINGS. CONNECTED.</p>
            <h1>
              Know the position.
              <br />
              <span>Own the next move.</span>
            </h1>
            <p className="hero-description">
              Turn scattered opening notes into a repertoire you understand. Build your lines,
              recognize transpositions, and bring your preparation back to the board.
            </p>
            <Link className="hero-cta" to="/login">
              Build your repertoire <ArrowRight size={18} />
            </Link>
            <p className="hero-footnote">
              <Check size={14} /> A personal workspace for your chess
            </p>
          </div>
          <div
            className="hero-preview"
            aria-label="Example of two move orders reaching the same Caro-Kann position"
          >
            <div className="preview-top">
              <span>
                <Layers size={16} /> REPERTOIRE STUDIO
              </span>
              <span className="example-tag">Example</span>
            </div>
            <h2>
              Different routes.
              <br />
              One familiar position.
            </h2>
            <div className="opening-path">
              <span className="path-label">KING'S PAWN</span>
              <div>
                <b>1. e4</b>
                <span>c6</span>
                <b>2. d4</b>
                <span>d5</span>
              </div>
            </div>
            <div className="opening-path">
              <span className="path-label">QUEEN'S PAWN</span>
              <div>
                <b>1. d4</b>
                <span>c6</span>
                <b>2. e4</b>
                <span>d5</span>
              </div>
            </div>
            <div className="preview-merge">
              <GitMerge size={22} />
              <div>
                <strong>Caro-Kann Defense</strong>
                <p>Same position. Your preparation still applies.</p>
              </div>
              <Check size={18} />
            </div>
            <p className="preview-caption">Study the idea once. Recognize it in every move order.</p>
          </div>
        </section>
        <section id="how-it-works" className="marketing-features">
          <header>
            <p className="studio-eyebrow">A CLEARER WAY TO PREPARE</p>
            <h2>
              Less collecting lines.
              <br />
              More understanding chess.
            </h2>
          </header>
          <div className="feature-grid">
            <article>
              <BookOpen />
              <span>01 / BUILD</span>
              <h3>Your repertoire, your way</h3>
              <p>
                Play through ideas on the board. Organize main lines and alternatives, and keep the
                notes that make them yours.
              </p>
            </article>
            <article>
              <GitMerge />
              <span>02 / CONNECT</span>
              <h3>See beyond move order</h3>
              <p>
                Follow different paths into familiar positions without losing where you came from.
              </p>
            </article>
            <article>
              <Compass />
              <span>03 / REFINE</span>
              <h3>Learn from your games</h3>
              <p>
                Import games, explore candidate moves, and find where your play leaves your
                preparation.
              </p>
            </article>
          </div>
        </section>
        <section className="marketing-close">
          <div>
            <p className="studio-eyebrow">MAKE YOUR NEXT SESSION COUNT</p>
            <h2>Give your preparation a home.</h2>
          </div>
          <Link className="hero-cta" to="/login">
            Get started <ArrowRight size={18} />
          </Link>
        </section>
      </main>
      <SiteFooter>
        <Link to="/login">Sign in</Link>
      </SiteFooter>
    </>
  );
}
